import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/apiClient'
import Button from '../components/Shared/Button'
import FeedbackCard from '../components/Shared/FeedbackCard'
import { ArrowRightOnRectangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

export default function SettingsPage() {
  const navigate = useNavigate()

  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  const fetchSession = async (opts = { silent: false }) => {
    if (!opts.silent) setLoading(true)
    setError('')
    try {
      const res = await api.get('/api/users/session')
      setSession(res.data)
    } catch (e) {
      setSession(null)
      setError('We couldn’t load your settings right now. You can still manage your account and try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const res = await api.get('/api/users/session')
        if (!ignore) setSession(res.data)
      } catch {
        if (!ignore) setSession(null)
        if (!ignore) setError('We couldn’t load your settings right now. Try again in a moment.')
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchSession({ silent: true })
    setRefreshing(false)
  }

  const logout = async () => {
    try {
      await api.post('/api/users/logout')
    } catch {
      // Even if logout fails, force user out locally
    } finally {
      window.location.href = '/login'
    }
  }

  const accessLabel = useMemo(() => {
    if (!session) return '—'
    if (session.subscription) return 'Creator Club'
    if (session.bundle) return `Bundle: ${session.bundle}`
    if ((session.templates?.length || 0) > 1 || (session.features?.length || 0) > 0) return 'Free app + design unlocks'
    return 'Complete app free'
  }, [session])

  const lastSeen = session?.last_login ? dayjs(session.last_login).fromNow() : '—'

  const featureChips = useMemo(() => {
    const list = Array.isArray(session?.features) ? session.features : []
    return list.slice(0, 6)
  }, [session])

  const extraFeatureCount = useMemo(() => {
    const n = Array.isArray(session?.features) ? session.features.length : 0
    return Math.max(0, n - 6)
  }, [session])

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-charcoal">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#5a3e36] tracking-tight">Settings</h1>
          <p className="mt-1 text-sm text-[#7a6f66]">
            The complete app is free. Manage your account and any additional recipe-card designs you own.
          </p>
        </div>

        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading || refreshing}
          className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-light-gray bg-white hover:bg-soft-yellow/30 transition disabled:opacity-60"
          title="Refresh access"
        >
          <ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Loading / error */}
      {loading && (
        <div className="mt-6 space-y-8 animate-pulse">
          {/* Profile skeleton */}
          <section className="rounded-2xl border border-light-gray bg-white shadow-sm p-5 sm:p-6">
            <div className="h-4 w-24 bg-soft-yellow/10 rounded" />
            <div className="mt-3 space-y-2">
              <div className="h-4 w-48 bg-soft-yellow/10 rounded" />
              <div className="h-4 w-32 bg-soft-yellow/10 rounded" />
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <div className="h-9 w-56 bg-soft-yellow/10 rounded-xl" />
              <div className="h-9 w-40 bg-soft-yellow/10 rounded-xl" />
            </div>
          </section>

          {/* Plan & Access skeleton */}
          <section className="rounded-2xl border border-light-gray bg-white shadow-sm p-5 sm:p-6">
            <div className="h-4 w-32 bg-soft-yellow/10 rounded mb-4" />
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-light-gray bg-soft-yellow/5 p-4">
                <div className="h-3 w-20 bg-soft-yellow/10 rounded" />
                <div className="mt-2 h-6 w-16 bg-soft-yellow/10 rounded" />
              </div>
              <div className="rounded-xl border border-light-gray bg-soft-yellow/5 p-4">
                <div className="h-3 w-20 bg-soft-yellow/10 rounded" />
                <div className="mt-2 h-6 w-16 bg-soft-yellow/10 rounded" />
              </div>
              <div className="rounded-xl border border-light-gray bg-soft-yellow/5 p-4">
                <div className="h-3 w-24 bg-soft-yellow/10 rounded" />
                <div className="mt-2 h-5 w-28 bg-soft-yellow/10 rounded" />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <div className="h-6 w-24 bg-soft-yellow/10 rounded-full border border-light-gray" />
              <div className="h-6 w-28 bg-soft-yellow/10 rounded-full border border-light-gray" />
              <div className="h-6 w-20 bg-soft-yellow/10 rounded-full border border-light-gray" />
            </div>
          </section>

          {/* Help skeleton */}
          <section className="rounded-2xl border border-light-gray bg-white shadow-sm p-5 sm:p-6">
            <div className="h-4 w-16 bg-soft-yellow/10 rounded mb-3" />
            <div className="space-y-2">
              <div className="h-4 w-72 bg-soft-yellow/10 rounded" />
              <div className="h-4 w-64 bg-soft-yellow/10 rounded" />
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <div className="h-8 w-44 bg-soft-yellow/10 rounded-xl border border-light-gray" />
              <div className="h-8 w-36 bg-soft-yellow/10 rounded-xl border border-light-gray" />
              <div className="h-8 w-28 bg-soft-yellow/10 rounded-xl border border-light-gray" />
            </div>
          </section>
        </div>
      )}

      {!loading && error && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-semibold">Heads up</div>
          <div className="mt-1">{error}</div>
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div className="mt-6 space-y-8">
        {/* Profile */}
        <section className="rounded-2xl border border-light-gray bg-white shadow-sm p-5 sm:p-6">
          <h2 className="text-base font-semibold text-[#5a3e36] mb-2">Profile</h2>

          <div className="text-sm leading-relaxed text-[#7a6f66]">
            <p>
              Email:{' '}
              <span className="text-[#5a3e36] font-medium">
                {session?.email || '—'}
              </span>
            </p>
            <p>
              Last active:{' '}
              <span className="text-[#5a3e36] font-medium">
                {session ? lastSeen : '—'}
              </span>
            </p>
          </div>

          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.open('https://ruothstore.com/account', '_blank')}
              className="justify-center"
            >
              Manage account on RuothStore →
            </Button>

            <Button
              type="button"
              variant="primary"
              onClick={() => navigate('/templates')}
              className="justify-center"
            >
              Browse templates
            </Button>
          </div>
        </section>

        {/* Plan & Access */}
        <section className="rounded-2xl border border-light-gray bg-white shadow-sm p-5 sm:p-6">
          <h2 className="text-base font-semibold mb-2 text-[#5a3e36]">Plan & access</h2>

          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${
                session?.subscription
                  ? 'bg-rich-gold text-white border-rich-gold'
                  : 'bg-gray-50 text-[#5a3e36] border-light-gray'
              }`}
            >
              {accessLabel}
            </span>

            {session?.subscription ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                Best value for active creators
              </span>
            ) : null}
          </div>

          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-light-gray bg-soft-yellow/5 p-4">
              <div className="text-xs text-[#7a6f66] font-semibold">Templates</div>
              <div className="mt-1 text-lg font-bold text-[#5a3e36]">
                {session?.subscription ? 'All' : (session?.templates?.length ?? 0)}
              </div>
            </div>

            <div className="rounded-xl border border-light-gray bg-soft-yellow/5 p-4">
              <div className="text-xs text-[#7a6f66] font-semibold">Core app</div>
              <div className="mt-1 text-lg font-bold text-[#5a3e36]">Included free</div>
            </div>

            <div className="rounded-xl border border-light-gray bg-soft-yellow/5 p-4">
              <div className="text-xs text-[#7a6f66] font-semibold">Access type</div>
              <div className="mt-1 text-sm font-semibold text-[#5a3e36]">
                {session?.subscription
                  ? 'Creator Club'
                  : session?.bundle
                    ? 'Bundle'
                    : (session?.templates?.length || session?.features?.length)
                      ? 'Individual unlocks'
                      : 'Starter'}
              </div>
            </div>
          </div>

          {/* Feature chips */}
          {Array.isArray(session?.features) && session.features.length > 0 ? (
            <div className="mt-4">
              <div className="text-xs font-semibold text-[#7a6f66]">Enabled tools</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {featureChips.map((f) => (
                  <span
                    key={f}
                    className="px-3 py-1 rounded-full text-xs font-semibold bg-white border border-light-gray text-[#5a3e36]"
                  >
                    {String(f)}
                  </span>
                ))}
                {extraFeatureCount > 0 ? (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-50 border border-light-gray text-[#7a6f66]">
                    +{extraFeatureCount} more
                  </span>
                ) : null}
              </div>
            </div>
          ) : session?.subscription ? (
            <div className="mt-4 text-sm text-[#7a6f66]">
              Your Creator Club membership unlocks the full premium design library. The app itself remains free.
            </div>
          ) : (
            <div className="mt-4 text-sm text-[#7a6f66]">
              {session?.subscription
                ? 'You’re in Creator Club, so the premium design library is unlocked. The app itself remains free.'
                : session?.bundle
                  ? `You’ve got ${session.bundle} bundle access, browse templates to use your included designs.`
                  : (session?.templates?.length || 0) > 0 || (session?.features?.length || 0) > 0
                    ? 'You’ve unlocked additional designs. Browse templates to use what you own or discover more.'
                    : 'The complete app and one recipe-card design are included free. Browse templates whenever you want another style.'}
            </div>
          )}
        </section>

        <FeedbackCard />

        {/* Help */}
        <section className="rounded-2xl border border-light-gray bg-white shadow-sm p-5 sm:p-6">
          <h2 className="text-base font-semibold mb-2 text-[#5a3e36]">Help</h2>
          <div className="text-sm text-[#7a6f66] space-y-2">
            <p>
              If your access looks wrong after a purchase, tap <span className="font-semibold">Refresh</span>.
            </p>
            <p>
              Community updates and new drops are posted at <span className="font-semibold">@ruothstore</span>.
            </p>
          </div>

          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.open('https://instagram.com/ruothstore', '_blank')}
              className="justify-center"
            >
              Follow @ruothstore →
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.open('/privacy-policy', '_blank')}
              className="justify-center"
            >
              Privacy policy
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.open('/terms', '_blank')}
              className="justify-center"
            >
              Terms
            </Button>
          </div>
        </section>

        {/* Logout */}
        <section className="pt-1">
          <Button
            type="button"
            variant="secondary"
            onClick={logout}
            className="justify-center gap-2 w-full sm:w-auto"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            Sign out
          </Button>
        </section>
        </div>
      )}
    </div>
  )
}
